import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useRef } from "react";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { useMyModerationStatus } from "../../modules/account";
import { MessagingDock } from "../../modules/social/components/MessagingDock";
import { AppBar, PLAYER_APP_BAR_HEIGHT } from "./AppBar";
import { ContextRail } from "./ContextRail";
import { PrimaryNavigation, type NavigationLayout } from "./PrimaryNavigation";
import { resolvePlayerRoute } from "../routeMetadata";
import { GuardianNotice } from "../components/GuardianNotice";
import { GuardianPrompt } from "../components/GuardianPrompt";

function useNavigationLayout(): NavigationLayout {
  const compact = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const expanded = useMediaQuery("(min-width:840px)", { noSsr: true });
  if (compact) return "compact";
  if (expanded) return "expanded";
  return "medium";
}

export function AppShell() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const layout = useNavigationLayout();
  const mainRef = useRef<HTMLElement | null>(null);
  const previousPath = useRef(pathname);
  const hasContext =
    layout === "expanded" && pathname === "/" && !loading && Boolean(user);
  const hasFullPageMessaging =
    resolvePlayerRoute(pathname)?.shell === "messaging";
  const hasDesktopMessagingWorkspace =
    hasFullPageMessaging && layout !== "compact";
  const drawerWidth =
    layout === "expanded" ? 224 : layout === "medium" ? 80 : 0;

  useEffect(() => {
    if (pathname === previousPath.current) return;
    previousPath.current = pathname;
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <Box
        component="a"
        href="#main"
        sx={{
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: "semantic.primary",
          color: "semantic.onPrimary",
          fontWeight: 800,
          textDecoration: "none",
          transform: "translateY(-160%)",
          "&:focus-visible": {
            transform: "translateY(0)",
          },
        }}
      >
        Skip to main content
      </Box>

      <AppBar />
      <PrimaryNavigation layout={layout} appBarHeight={PLAYER_APP_BAR_HEIGHT} />

      <Box
        component="main"
        id="main"
        ref={mainRef}
        tabIndex={-1}
        sx={{
          minWidth: 0,
          minHeight: "100dvh",
          ml: `${drawerWidth}px`,
          pt: PLAYER_APP_BAR_HEIGHT,
          pb:
            layout === "compact"
              ? "calc(4.75rem + env(safe-area-inset-bottom, 0px))"
              : hasFullPageMessaging
                ? 0
                : 4,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: "100%",
            // Approved shell: a 620-720px content column plus the 296px rail.
            maxWidth: hasFullPageMessaging ? "none" : hasContext ? 1100 : 1120,
            px: hasFullPageMessaging ? 0 : { xs: 1.5, sm: 3, lg: 3 },
            py: hasFullPageMessaging ? 0 : { xs: 2, sm: 3 },
            display: hasDesktopMessagingWorkspace ? "flex" : undefined,
            flexDirection: hasDesktopMessagingWorkspace ? "column" : undefined,
            minHeight: hasDesktopMessagingWorkspace
              ? `calc(100dvh - ${PLAYER_APP_BAR_HEIGHT})`
              : undefined,
            height: hasDesktopMessagingWorkspace
              ? `calc(100dvh - ${PLAYER_APP_BAR_HEIGHT})`
              : undefined,
          }}
        >
          <ModerationNotice />
          {hasFullPageMessaging ? null : <GuardianNotice />}
          {hasContext ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  lg: "minmax(0, 720px) 296px",
                },
                alignItems: "start",
                gap: 3,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Outlet />
              </Box>
              <ContextRail />
            </Box>
          ) : hasDesktopMessagingWorkspace ? (
            <Box sx={{ display: "flex", flex: 1, minWidth: 0, minHeight: 0 }}>
              <Outlet />
            </Box>
          ) : (
            <Outlet />
          )}
        </Container>
      </Box>

      {layout === "expanded" && !hasFullPageMessaging ? (
        <MessagingDock />
      ) : null}
      <GuardianPrompt />
    </Box>
  );
}

function ModerationNotice() {
  const { user } = useAuth();
  const status = useMyModerationStatus(Boolean(user));
  if (!status.data?.is_banned) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Some interaction features are restricted. Public reads remain available.{" "}
      <Link component={RouterLink} to="/me/moderation" color="inherit">
        Review restriction and appeals
      </Link>
    </Alert>
  );
}
